import React, { useState, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import { AssignmentLate, CheckCircleOutline } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import {
  useMyNotifications,
  type AppNotification,
} from '@/hooks/queries/notifications/useMyNotifications';
import { useNotificationReadStore } from '@/stores/useNotificationReadStore';
import { useMyPersonEntityId } from '@/hooks/useMyPersonEntityId';
import { useNotificationLabels } from '@/hooks/useNotificationLabels';
import dayjs from 'dayjs';

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const NOTIFICATION_CHIP_COLOR: Record<
  AppNotification['type'],
  'error' | 'warning' | 'info' | 'default'
> = {
  thr_overdue: 'error',
  expired: 'warning',
  expiring_soon: 'warning',
  boi_changes: 'info',
};

const fmtRelativeDate = (date: string | null): string => {
  if (!date) return '';
  return dayjs(date).format('MMM D, YYYY');
};

interface NotificationCardProps {
  notification: AppNotification;
  isRead: boolean;
  onMarkRead: (id: string) => void;
}

const NotificationCard: React.FC<NotificationCardProps> = ({
  notification,
  isRead,
  onMarkRead,
}) => {
  const navigate = useNavigate();
  const color = NOTIFICATION_CHIP_COLOR[notification.type];

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.5,
        borderRadius: 2,
        borderColor: isRead
          ? 'divider'
          : color === 'error'
            ? 'error.light'
            : color === 'info'
              ? 'info.light'
              : 'warning.light',
        bgcolor: (theme: Theme) =>
          isRead
            ? theme.palette.background.paper
            : color === 'error'
              ? alpha(theme.palette.error.main, 0.08)
              : color === 'info'
                ? alpha(theme.palette.info.main, 0.08)
                : alpha(theme.palette.warning.main, 0.08),
        opacity: isRead ? 0.75 : 1,
        transition: 'all 0.2s',
      }}
    >
      {/* Header row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Chip
          label={notification.label}
          color={color}
          size="small"
          variant="outlined"
          sx={{ fontWeight: 600, fontSize: '0.7rem' }}
        />
        {!isRead && (
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor:
                color === 'error' ? 'error.main' : color === 'info' ? 'info.main' : 'warning.main',
              flexShrink: 0,
            }}
          />
        )}
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          {fmtRelativeDate(notification.date)}
        </Typography>
      </Box>

      {/* Title */}
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.75, lineHeight: 1.3 }}>
        {notification.title}
      </Typography>
      {/* Body */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.5 }}>
        {notification.body}
      </Typography>

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={() => {
            onMarkRead(notification.id);
            navigate(notification.actionRoute);
          }}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderColor:
              color === 'error' ? 'error.main' : color === 'info' ? 'info.main' : 'warning.main',
            color:
              color === 'error' ? 'error.main' : color === 'info' ? 'info.main' : 'warning.main',
            '&:hover': {
              borderColor:
                color === 'error' ? 'error.dark' : color === 'info' ? 'info.dark' : 'warning.dark',
              bgcolor: (theme: Theme) =>
                color === 'error'
                  ? alpha(theme.palette.error.main, 0.08)
                  : color === 'info'
                    ? alpha(theme.palette.info.main, 0.08)
                    : alpha(theme.palette.warning.main, 0.08),
            },
          }}
        >
          {notification.actionLabel}
        </Button>
        {!isRead && (
          <Button
            variant="text"
            size="small"
            onClick={() => onMarkRead(notification.id)}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            Mark read
          </Button>
        )}
      </Box>
    </Paper>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
type TabValue = 'all' | 'unread';

/**
 * @param title - Optional page title override (default: 'Notifications').
 *
 * Every notifications fetch (the service-member-only /notifications route and
 * the personal /my-notifications route) always sends an explicit person_id
 * filter for the current session's linked person entity, rather than relying
 * solely on the DB view's RLS-style WHERE clause. This is a fail-closed design
 * (#347, #1009): it ensures impersonated and dual-role sessions never see
 * another user's notifications even if server-side role/identity resolution
 * has an edge case.
 */
export const NotificationsPage: React.FC<{
  title?: string;
}> = ({ title = 'Notifications' }) => {
  usePageTitle(title);
  const [tab, setTab] = useState<TabValue>('all');

  // Explicit person scoping (fail-closed, #347/#1009): the current session's
  // linked person entity id is always sent as a person_id filter.
  const { myPersonEntityId } = useMyPersonEntityId();
  const personEntityId = myPersonEntityId;
  const labels = useNotificationLabels();

  const { notifications, isLoading, isError } = useMyNotifications({
    ...(personEntityId !== null ? { personEntityId } : {}),
    labels,
  });
  const { markRead, markAllRead, isRead } = useNotificationReadStore();

  const unread = useMemo(() => notifications.filter((n) => !isRead(n.id)), [notifications, isRead]);

  const displayed = tab === 'all' ? notifications : unread;

  const handleMarkAllRead = () => {
    markAllRead(notifications.map((n) => n.id));
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Page header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Notifications
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              Alerts requiring your attention — overdue items, expirations, and{' '}
              {labels.summaryPhrase}.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
            {unread.length > 0 && (
              <>
                <Button
                  variant="text"
                  size="small"
                  startIcon={<CheckCircleOutline fontSize="small" />}
                  onClick={handleMarkAllRead}
                  sx={{ textTransform: 'none', color: 'text.secondary' }}
                >
                  Mark all read
                </Button>
                <Typography variant="body2" fontWeight={700} color="error.main">
                  {unread.length} unread
                </Typography>
              </>
            )}
          </Box>
        </Box>

        {/* Tabs */}
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
            <Tabs
              value={tab}
              onChange={(_, v: TabValue) => setTab(v)}
              indicatorColor="primary"
              textColor="primary"
            >
              <Tab
                value="all"
                label={`All (${notifications.length})`}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              />
              <Tab
                value="unread"
                label={`Unread (${unread.length})`}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              />
            </Tabs>
          </Box>

          {/* Content */}
          <Box sx={{ p: 2 }}>
            {isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            )}

            {isError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Failed to load notifications. Please try again.
              </Alert>
            )}

            {!isLoading && !isError && displayed.length === 0 && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  py: 8,
                  color: 'text.secondary',
                }}
              >
                <AssignmentLate sx={{ fontSize: 48, mb: 1.5, opacity: 0.4 }} />
                <Typography variant="body1" fontWeight={600}>
                  {tab === 'unread' ? 'All caught up!' : 'No notifications'}
                </Typography>
                <Typography variant="body2">
                  {tab === 'unread'
                    ? 'You have no unread notifications.'
                    : 'There are no alerts at this time.'}
                </Typography>
              </Box>
            )}

            {!isLoading && displayed.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {displayed.map((n) => (
                  <NotificationCard
                    key={n.id}
                    notification={n}
                    isRead={isRead(n.id)}
                    onMarkRead={markRead}
                  />
                ))}
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};
